<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

  interface MorphPopoverLayerProps {
    target?: HTMLElement | null;
    visible: boolean;
  }

  const props = withDefaults(defineProps<MorphPopoverLayerProps>(), {
    target: null,
  });

  const anchorRef = ref<HTMLElement | null>(null);
  const contentRef = ref<HTMLElement | null>(null);

  function placeContent(): void {
    const content = contentRef.value;
    const parent = props.target ?? anchorRef.value;
    if (!content || !parent) {
      return;
    }
    if (content.parentElement === parent) {
      return;
    }
    parent.append(content);
  }

  // Уходящий слой лежит поверх нового, пока гаснет: клик в него попал бы в невидимую выпадашку прежнего поля.
  function disablePointer(element: Element): void {
    const { style } = element as HTMLElement;
    style.pointerEvents = 'none';
  }

  onMounted(() => {
    placeContent();
    watch(() => props.target, placeContent);
  });

  // Vue при размонтировании снимает только корень компонента — перенесённое содержимое осталось бы в карточке.
  onBeforeUnmount(() => {
    if (!contentRef.value || !anchorRef.value) {
      return;
    }
    anchorRef.value.append(contentRef.value);
  });
</script>

<template>
  <div
    ref="anchorRef"
    class="morph-popover-layer"
  >
    <div
      ref="contentRef"
      class="morph-popover-layer__content"
    >
      <transition
        name="morph-popover-layer"
        @before-leave="disablePointer"
      >
        <div
          v-if="visible"
          class="morph-popover-layer__view"
        >
          <slot />
        </div>
      </transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .morph-popover-layer {
    &-enter-active {
      transition: opacity 0.3s ease 0.14s;
    }

    &-leave-active {
      transition: opacity 0.16s ease;
    }

    &-enter,
    &-leave-to {
      opacity: 0;
    }

    @media (prefers-reduced-motion: reduce) {
      &-enter-active,
      &-leave-active {
        transition: none;
      }
    }
  }
</style>
